import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UMA_PRESETS_4P, calculateMatch, calculateOka, isValidUma } from '@mahjong/core';
import type { Rounding, Rule, RuleSet, UmaPreset } from '@mahjong/core';
import {
  AppBar,
  BarAction,
  Body,
  Card,
  Chip,
  ErrorBox,
  Label,
  ListCard,
  RankBadge,
  Screen,
  Spinner,
  cx,
} from '../components/ui';
import { useGroup, useGroupToken } from '../hooks/useGroup';
import { api, ApiError } from '../lib/api';
import { formatPoint, formatScore } from '../lib/format';

const ROUNDING_LABELS: Record<Rounding, string> = {
  gosha_rokunyu: '五捨六入',
  shisha_gonyu: '四捨五入',
  ceil: '切り上げ',
  floor: '切り捨て',
};

const PRESET_LABELS: Record<Exclude<UmaPreset, 'custom'>, string> = {
  gotto: '5-10',
  one_two: '10-20',
  one_three: '10-30',
  two_three: '20-30',
};

/**
 * ルール設定（画面案7 / §4.3）。
 *
 * オカは返し点と原点から自動で決まる値なので、入力欄ではなく結果表示にする。
 * 設定を変えても過去の対局は変わらない（対局はルールのスナップショットを持つ）。
 */
export function RulesPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const token = useGroupToken(groupId);
  const group = useGroup(groupId, token);
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<Rule | null>(null);
  const [ruleId, setRuleId] = useState<string | null>(null);
  const [preset, setPreset] = useState<UmaPreset>('custom');
  const [ruleName, setRuleName] = useState('');

  const loaded = group.data;

  /** 指定したルールを編集対象として読み込む */
  function loadRule(rs: RuleSet | undefined) {
    if (!rs) return;
    setRuleId(rs.ruleId);
    setRuleName(rs.name);
    setPreset(rs.umaPreset);
    setDraft({
      playerCount: rs.playerCount,
      startPoints: rs.startPoints,
      returnPoints: rs.returnPoints,
      uma: [...rs.uma],
      rounding: rs.rounding,
      zeroSumAdjust: rs.zeroSumAdjust,
      bankruptcy: { ...rs.bankruptcy },
    });
  }

  // 最初は既定ルールを開く
  useEffect(() => {
    if (!loaded || draft) return;
    loadRule(loaded.rules.find((r) => r.ruleId === loaded.group.defaultRuleId) ?? loaded.rules[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, draft]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['group', groupId] });

  const save = useMutation({
    mutationFn: () =>
      api.updateRule(groupId!, token!, ruleId!, { ...draft!, name: ruleName, umaPreset: preset }),
    onSuccess: () => {
      void refresh();
      navigate(`/g/${groupId}`);
    },
  });

  const addRule = useMutation({
    mutationFn: () =>
      api.createRule(groupId!, token!, { ...draft!, name: `${ruleName}のコピー`, umaPreset: preset }),
    onSuccess: (created) => {
      void refresh();
      loadRule(created);
    },
  });

  const removeRule = useMutation({
    mutationFn: (id: string) => api.deleteRule(groupId!, token!, id),
    onSuccess: () => {
      void refresh();
      setDraft(null); // 既定ルールを開き直す
    },
  });

  const setDefault = useMutation({
    mutationFn: (id: string) => api.updateGroup(groupId!, token!, { defaultRuleId: id }),
    onSuccess: () => void refresh(),
  });

  if (!token) {
    return (
      <Screen>
        <AppBar title="ルール設定" backTo="/" />
        <Body>
          <ErrorBox message="この端末に参加情報がありません。共有URLから開いてください。" />
        </Body>
      </Screen>
    );
  }

  if (group.isLoading || !draft) {
    return (
      <Screen>
        <AppBar title="ルール設定" backTo={`/g/${groupId}`} />
        <Body>
          <Spinner />
        </Body>
      </Screen>
    );
  }

  const update = (patch: Partial<Rule>) => setDraft({ ...draft, ...patch });
  const oka = calculateOka(draft);
  const umaValid = isValidUma(draft.uma, draft.playerCount);
  const umaSum = draft.uma.reduce((a, b) => a + b, 0);

  function changePlayerCount(n: 3 | 4) {
    if (n === draft!.playerCount) return;
    if (n === 4) {
      update({
        playerCount: 4,
        startPoints: 25_000,
        returnPoints: 30_000,
        uma: [...UMA_PRESETS_4P.one_two],
      });
      setPreset('one_two');
    } else {
      update({ playerCount: 3, startPoints: 35_000, returnPoints: 40_000, uma: [20, 0, -20] });
      setPreset('custom');
    }
  }

  function applyPreset(key: Exclude<UmaPreset, 'custom'>) {
    setPreset(key);
    update({ uma: [...UMA_PRESETS_4P[key]] });
  }

  function setUmaAt(i: number, value: number) {
    const next = [...draft!.uma];
    next[i] = value;
    setPreset('custom');
    update({ uma: next });
  }

  // --- 「この設定での計算例」（§9.2） ---------------------------------------
  const sample =
    draft.playerCount === 4
      ? [
          draft.startPoints * 4 - 55_000,
          32_000,
          25_000,
          -2_000 + (draft.startPoints * 4 - 100_000),
        ]
      : [draft.startPoints * 3 - 50_000, 32_000, 18_000];
  const sampleInputs = sample.map((rawScore, i) => ({
    memberId: `s${i}`,
    nameSnapshot: ['Aさん', 'Bさん', 'Cさん', 'Dさん'][i]!,
    rawScore,
  }));
  const samplePreview = calculateMatch(sampleInputs, draft);

  return (
    <Screen>
      <AppBar
        title={ruleName || 'ルール設定'}
        backTo={`/g/${groupId}`}
        action={
          <BarAction
            onClick={() => save.mutate()}
            disabled={!umaValid || save.isPending}
            className={cx(!umaValid && 'text-felt-sub')}
          >
            {save.isPending ? '保存中…' : '保存'}
          </BarAction>
        }
      />

      <Body className="gap-2.5">
        {/* --- ルールセットの切り替え（§4.3） --- */}
        {loaded && loaded.rules.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {loaded.rules.map((r) => (
              <Chip
                key={r.ruleId}
                selected={r.ruleId === ruleId}
                onClick={() => loadRule(r)}
              >
                {r.name}
                {r.ruleId === loaded.group.defaultRuleId && ' ★'}
              </Chip>
            ))}
          </div>
        )}

        {/* --- ルール名 --- */}
        <div>
          <Label>ルール名</Label>
          <input
            value={ruleName}
            onChange={(e) => setRuleName(e.target.value)}
            maxLength={30}
            className="mt-1 w-full rounded-lg border border-line bg-white px-3 py-2.5 text-[15px] outline-none focus:border-felt"
          />
        </div>

        {/* --- 既定ルールの指定 --- */}
        {loaded && ruleId && ruleId !== loaded.group.defaultRuleId && (
          <button
            type="button"
            onClick={() => setDefault.mutate(ruleId)}
            disabled={setDefault.isPending}
            className="self-start text-[12.5px] font-medium text-felt underline underline-offset-2"
          >
            このルールを既定にする
          </button>
        )}

        {/* --- 人数 --- */}
        <div className="flex overflow-hidden rounded-lg border border-line bg-white">
          {([4, 3] as const).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => changePlayerCount(n)}
              className={cx(
                'flex-1 py-2 text-[13px] transition',
                n === 3 && 'border-l border-line',
                draft.playerCount === n ? 'bg-felt font-medium text-bone-hi' : 'text-ink-soft',
              )}
            >
              {n}人打ち
            </button>
          ))}
        </div>

        {/* --- 点数 --- */}
        <ListCard className="py-1">
          <NumberRow
            label="配給原点"
            hint="開始時の持ち点"
            value={draft.startPoints}
            onChange={(v) => update({ startPoints: v })}
          />
          <NumberRow
            label="返し点"
            hint="精算で差し引く基準点"
            value={draft.returnPoints}
            onChange={(v) => update({ returnPoints: v })}
          />
          <div className="flex items-center gap-3 border-t border-line py-3">
            <div>
              <div className="text-[13.5px]">オカ</div>
              <div className="mt-0.5 text-[11px] text-ink-faint">自動計算・トップが総取り</div>
            </div>
            <span className="ml-auto text-sm font-bold tabular-nums text-ink-faint">
              {formatPoint(oka)}
            </span>
          </div>
        </ListCard>

        {/* --- ウマ --- */}
        <Label className="mt-1">ウマ</Label>
        {draft.playerCount === 4 && (
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(PRESET_LABELS) as Exclude<UmaPreset, 'custom'>[]).map((key) => (
              <Chip key={key} selected={preset === key} onClick={() => applyPreset(key)}>
                {PRESET_LABELS[key]}
              </Chip>
            ))}
            <Chip selected={preset === 'custom'} onClick={() => setPreset('custom')}>
              自由入力
            </Chip>
          </div>
        )}
        <div
          className={cx('grid gap-1.5', draft.playerCount === 4 ? 'grid-cols-4' : 'grid-cols-3')}
        >
          {draft.uma.map((u, i) => (
            <div key={i} className="rounded-lg border border-line bg-white py-2 text-center">
              <div className="text-[10px] text-ink-faint">{i + 1}位</div>
              <input
                value={u}
                onChange={(e) => setUmaAt(i, Number(e.target.value.replace(/[^0-9-]/g, '')) || 0)}
                inputMode="numeric"
                aria-label={`${i + 1}位のウマ`}
                className={cx(
                  'w-full bg-transparent text-center text-[15px] font-black tabular-nums outline-none',
                  u > 0 ? 'pt-plus' : u < 0 ? 'pt-minus' : 'text-ink-soft',
                )}
              />
            </div>
          ))}
        </div>
        {!umaValid && (
          <ErrorBox
            message={`ウマの合計は0にしてください（現在 ${formatPoint(umaSum)}）。合計が0でないと全員の精算ポイントが釣り合いません。`}
          />
        )}

        {/* --- 端数処理 --- */}
        <Label className="mt-1">端数処理</Label>
        <div className="flex flex-wrap gap-1.5">
          {(Object.keys(ROUNDING_LABELS) as Rounding[]).map((r) => (
            <Chip key={r} selected={draft.rounding === r} onClick={() => update({ rounding: r })}>
              {ROUNDING_LABELS[r]}
            </Chip>
          ))}
        </div>

        {/* --- 飛び賞 --- */}
        <ListCard className="mt-1 py-1">
          <div className="flex items-center gap-3 py-3">
            <div>
              <div className="text-[13.5px]">飛び賞</div>
              <div className="mt-0.5 text-[11px] text-ink-faint">
                {draft.bankruptcy.threshold === 'below_zero'
                  ? '0点未満で終了した人が支払う'
                  : '0点以下で終了した人が支払う'}
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={draft.bankruptcy.enabled}
              aria-label="飛び賞"
              onClick={() =>
                update({
                  bankruptcy: { ...draft.bankruptcy, enabled: !draft.bankruptcy.enabled },
                })
              }
              className={cx(
                'relative ml-auto h-[22px] w-[38px] shrink-0 rounded-full transition',
                draft.bankruptcy.enabled ? 'bg-felt' : 'bg-[#CFC8B6]',
              )}
            >
              <span
                className={cx(
                  'absolute top-[3px] size-4 rounded-full bg-white transition-all',
                  draft.bankruptcy.enabled ? 'right-[3px]' : 'left-[3px]',
                )}
              />
            </button>
          </div>

          {draft.bankruptcy.enabled && (
            <>
              <NumberRow
                label="支払うポイント"
                value={draft.bankruptcy.point}
                onChange={(v) => update({ bankruptcy: { ...draft.bankruptcy, point: v } })}
              />
              <div className="border-t border-line py-3">
                <div className="text-[13.5px]">受け取る人</div>
                <div className="mt-2 flex gap-1.5">
                  <Chip
                    selected={draft.bankruptcy.payTo === 'buster'}
                    onClick={() =>
                      update({ bankruptcy: { ...draft.bankruptcy, payTo: 'buster' } })
                    }
                  >
                    飛ばした人
                  </Chip>
                  <Chip
                    selected={draft.bankruptcy.payTo === 'top'}
                    onClick={() => update({ bankruptcy: { ...draft.bankruptcy, payTo: 'top' } })}
                  >
                    トップ
                  </Chip>
                </div>
              </div>
              {draft.bankruptcy.payTo === 'buster' && (
                <div className="border-t border-line py-3">
                  <div className="text-[13.5px]">飛ばした人が不明なとき</div>
                  <div className="mt-0.5 text-[11px] text-ink-faint">
                    流局・ノーテン罰符で飛んだ場合など
                  </div>
                  <div className="mt-2 flex gap-1.5">
                    <Chip
                      selected={draft.bankruptcy.fallbackPayTo === 'top'}
                      onClick={() =>
                        update({ bankruptcy: { ...draft.bankruptcy, fallbackPayTo: 'top' } })
                      }
                    >
                      トップが受け取る
                    </Chip>
                    <Chip
                      selected={draft.bankruptcy.fallbackPayTo === 'none'}
                      onClick={() =>
                        update({ bankruptcy: { ...draft.bankruptcy, fallbackPayTo: 'none' } })
                      }
                    >
                      授受なし
                    </Chip>
                  </div>
                </div>
              )}
              <div className="border-t border-line py-3">
                <div className="text-[13.5px]">飛びの判定</div>
                <div className="mt-2 flex gap-1.5">
                  <Chip
                    selected={draft.bankruptcy.threshold === 'below_zero'}
                    onClick={() =>
                      update({ bankruptcy: { ...draft.bankruptcy, threshold: 'below_zero' } })
                    }
                  >
                    0点未満
                  </Chip>
                  <Chip
                    selected={draft.bankruptcy.threshold === 'zero_or_below'}
                    onClick={() =>
                      update({ bankruptcy: { ...draft.bankruptcy, threshold: 'zero_or_below' } })
                    }
                  >
                    0点以下
                  </Chip>
                </div>
              </div>
            </>
          )}
        </ListCard>

        {/* --- この設定での計算例（§9.2） --- */}
        <Label className="mt-1">この設定での計算例</Label>
        <Card className="bg-felt text-bone-hi">
          {samplePreview.results.map((r) => (
            <div key={r.memberId} className="flex items-center gap-2.5 py-1 text-[13px]">
              <RankBadge rank={r.rank} className="size-[19px] text-[11px]" />
              <span>{r.nameSnapshot}</span>
              <span className="text-[11px] text-felt-sub">{formatScore(r.rawScore)}</span>
              <span
                className={cx(
                  'ml-auto text-[15px] font-black tabular-nums',
                  r.totalPoint >= 0 ? 'text-[#7FD9A8]' : 'text-[#F09A92]',
                )}
              >
                {formatPoint(r.totalPoint)}
              </span>
            </div>
          ))}
        </Card>

        <Label className="leading-relaxed">
          設定を変更しても、すでに記録した対局の計算結果は変わりません。
        </Label>

        {/* --- ルールセットの追加・削除 --- */}
        <div className="mt-2 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => addRule.mutate()}
            disabled={addRule.isPending || !umaValid}
            className="self-start text-[12.5px] font-medium text-felt underline underline-offset-2 disabled:text-ink-faint"
          >
            ＋ この内容で新しいルールを作る
          </button>
          {loaded && ruleId && loaded.rules.length > 1 && ruleId !== loaded.group.defaultRuleId && (
            <button
              type="button"
              onClick={() => removeRule.mutate(ruleId)}
              disabled={removeRule.isPending}
              className="self-start text-[12.5px] text-red underline underline-offset-2"
            >
              このルールを削除する
            </button>
          )}
        </div>

        {(addRule.isError || removeRule.isError || setDefault.isError) && (
          <ErrorBox
            message={
              [addRule.error, removeRule.error, setDefault.error].find(
                (e): e is ApiError => e instanceof ApiError,
              )?.message ?? '操作できませんでした'
            }
          />
        )}

        {save.isError && (
          <ErrorBox
            title="保存できませんでした"
            message={save.error instanceof ApiError ? save.error.message : '通信に失敗しました'}
          />
        )}
      </Body>
    </Screen>
  );
}

function NumberRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-3 border-t border-line py-3 first:border-t-0">
      <div>
        <div className="text-[13.5px]">{label}</div>
        {hint && <div className="mt-0.5 text-[11px] text-ink-faint">{hint}</div>}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(Number(e.target.value.replace(/[^0-9]/g, '')) || 0)}
        inputMode="numeric"
        aria-label={label}
        className="ml-auto w-24 bg-transparent text-right text-sm font-bold tabular-nums outline-none"
      />
    </div>
  );
}
